import createLayoutCheckout from '@components/LayoutCheckout';
import { useCheckoutCartId } from '@monofront/business/contexts';
import CheckoutDelivery from '@screens/CheckoutDelivery';
import withAuthentication from '@hocs/withAuthentication';
import { useShoppingCart } from '@hooks/cart/useShoppingCart';
import { useEffect } from 'react';
import { prismicService } from '@services/businessContainer';
import {
  formatCheckoutProductToKlaviyo,
  formatPrismicData,
} from '@monofront/business/utils';
import { ICheckoutPageContent } from '@monofront/types';
import { GetStaticProps } from 'next';
import { setFaroSessionAttribute } from '@utils/faro';
import { klaviyoTrackEvent } from '@utils/tracking';
import { ICheckoutSteps } from '@hooks/useCheckoutSteps';
import { OneTrustCookieCategories } from '@components/Vendors/Vendors';
import { LFPage, injectLayoutData } from '@utils/layout';
import getCookieConsent from '@utils/getCookieConsent';
import { isMobileApp } from '@utils/environment';
import { EVENT_SOURCE } from '@monofront/business/constants';
import {
  CheckoutSelectAddressMachineProvider,
  useCheckoutCartMachine,
} from '@context/checkoutCartMachineContext';
import { useEffectOnce } from '@monofront/business/hooks';
import HeartLoader from '@monofront/design-system/components/HeartLoader/HeartLoader';

interface IDeliveryPageProps {
  pageContents?: Partial<ICheckoutPageContent>;
}

const DeliveryPage: {
  getLayout: () => void;
  hasAppMobileLayout: boolean;
} & LFPage<IDeliveryPageProps> = ({ pageContents }) => {
  const [checkoutCartId] = useCheckoutCartId();
  const { startCheckout, canStartOrRestartCheckout } = useCheckoutCartMachine();
  const { shoppingCart, shoppingCartId } = useShoppingCart();

  useEffectOnce(
    () => shoppingCart?.products?.length > 0 && canStartOrRestartCheckout(),
    function startCheckoutEffect() {
      startCheckout(shoppingCart.products);
    },
    [shoppingCart?.products],
  );

  useEffect(() => {
    // If the user consents to performances cookies
    if (getCookieConsent(OneTrustCookieCategories.ANALYTICS)) {
      setFaroSessionAttribute('checkoutCartId', checkoutCartId);
    }
  }, [checkoutCartId]);

  useEffectOnce(
    () => shoppingCart.products?.length > 0,
    function klaviyoCheckoutStartedTrackingEffect() {
      const itemNames: string[] = [];

      const klaviyoItems = shoppingCart.products.map((product) => {
        itemNames.push(product.title);

        return formatCheckoutProductToKlaviyo(product);
      });

      klaviyoTrackEvent({
        name: 'Checkout Started',
        data: {
          $event_id: `${shoppingCartId}_${Math.floor(Date.now() / 1000)}`,
          $value: Math.floor(shoppingCart.totalPrice * 100) / 100,
          eventSource: isMobileApp ? EVENT_SOURCE.APP : EVENT_SOURCE.WEB,
          extra: {
            line_items: klaviyoItems,
          },
          Items: itemNames,
        },
      });
    },
    [shoppingCart],
  );

  if (!canStartOrRestartCheckout()) {
    return <HeartLoader />;
  }

  return (
    <CheckoutSelectAddressMachineProvider>
      <CheckoutDelivery pageContents={pageContents} />
    </CheckoutSelectAddressMachineProvider>
  );
};

DeliveryPage.getLayout = () => createLayoutCheckout(ICheckoutSteps.DELIVERY);
DeliveryPage.hasAppMobileLayout = true;

export const getStaticProps = injectLayoutData<
  IDeliveryPageProps,
  GetStaticProps<IDeliveryPageProps>
>(async ({ previewData, locale }) => {
  const response = await prismicService.getDocumentsByCustomTypeName(
    'checkout',
    previewData,
    locale ? { lang: `${locale}-${locale}` } : {},
  );

  const pageContents = response?.results[0]?.data
    ? formatPrismicData(response.results[0].data)
    : null;

  return {
    props: {
      pageContents,
    },
    revalidate: 1800, // 30min
  };
});

export default withAuthentication(DeliveryPage);
